// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import * as tracker from 'presentations/views/helpers/analytics';

import { useState, SyntheticEvent, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';

import { get, post, destroy, patch } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  apiPath,
  hostServiceEditPath,
  hostServiceNewPath,
} from 'presentations/routes/path';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import { useRequest } from 'presentations/views/hooks/request';
import { ServiceOptionIndex } from 'presentations/views/components/organisms/ServiceList';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import {
  ServiceDetailListItem,
  ServiceListItem,
} from 'presentations/api/schema/service';
import { IntegrationMergedTeamShopAccountItem } from 'presentations/api/schema/team_shop_account';
import { BusinessProfileItem } from 'presentations/api/schema/business_profile';

interface Props {
  viewer: ViewerItem;
}
const perPage = 20;

export function useServiceListPage(props: Props) {
  const t = translateInComponent(translations);

  const { state: navigationState, dispatch: navigationDispatch } = useContext(
    DashboardPortalContext,
  );

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [deleteService, setDeleteService] = useState<ServiceListItem | null>(
    null,
  );
  const [
    showEventTypeService,
    setShowEventTypeService,
  ] = useState<ServiceDetailListItem | null>(null);
  const [anchor, setAnchor] = useState<any>(null);
  const [
    shopAccount,
    setShopAccount,
  ] = useState<IntegrationMergedTeamShopAccountItem | null>(null);
  const [
    businessProfile,
    setBusinessProfile,
  ] = useState<BusinessProfileItem | null>(null);
  const [isAccountLoading, setIsAccountLoading] = useState<boolean>(false);

  const router = useRouter();

  useEffect(() => {
    (async () => {
      setIsAccountLoading(true);
      const shopAccountResult = await get(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/shop_account`),
      );
      setShopAccount(shopAccountResult.data || null);

      const businessProfileResult = await get(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/business_profile`),
      );

      setBusinessProfile(businessProfileResult.data || null);
      setIsAccountLoading(false);
    })();
  }, []);

  const servicesRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/services`, {
      page: currentPage,
      perPage,
    }),
  );
  const services: ServiceDetailListItem[] = servicesRequest.data || [];

  const subscriptionStateRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;

  const onChangePage = async (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDeleteService = async () => {
    if (!deleteService) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/services/${
          deleteService.id
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteService(null);
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(t('Failed Delete Service')));
      return;
    }

    const newServices = services.filter((e) => e.id !== deleteService.id);
    servicesRequest.mutate({ ...servicesRequest, data: newServices }, false);
    setDeleteService(null);
    setIsConfirmLoading(false);
    tracker.setEvent('service delete');
  };

  const onClickNewService = async (e: SyntheticEvent<HTMLButtonElement>) => {
    router.push(hostServiceNewPath());
  };

  const onCloseDialog = () => {
    setDeleteService(null);
  };

  const onSelectMenu = async (
    service: ServiceListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case ServiceOptionIndex.edit: {
        router.push(
          hostServiceEditPath({
            serviceId: service.uid,
          }),
        );
        break;
      }
      case ServiceOptionIndex.delete: {
        setDeleteService(service);
        break;
      }
      default:
        break;
    }
  };

  const onClickShowEventTypes = (service: ServiceDetailListItem) => {
    setShowEventTypeService(service);
  };

  const onCloseEventTypeServiceDialog = () => {
    setShowEventTypeService(null);
  };

  return {
    state: {
      shopAccount,
      businessProfile,
      services,
      isAccountLoading,
      isLoading: servicesRequest.isLoading,
      currentPage,
      numberOfPages: servicesRequest.numberOfPages,
      isConfirmLoading,
      deleteService,
      showEventTypeService,
      anchor,
    },
    actions: {
      onChangePage,
      onSelectMenu,
      onDeleteService,
      onCloseDialog,
      onClickNewService,
      onClickShowEventTypes,
      onCloseEventTypeServiceDialog,
    },
  };
}
